import React, { useEffect } from 'react';
import { useAdmin } from '../../hooks/useAdmin';
import styles from '../../styles/SystemMonitor.module.css';

const SystemMonitor: React.FC = () => {
  const {
    health,
    systemInfo,
    healthLoading,
    systemLoading,
    healthError,
    systemError,
    refreshHealth,
    refreshSystemInfo,
    clearHealthError,
    clearSystemError,
  } = useAdmin();

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      refreshHealth();
      refreshSystemInfo();
    }, 30000);

    return () => clearInterval(interval);
  }, [refreshHealth, refreshSystemInfo]);

  const formatUptime = (seconds: number): string => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    if (days > 0) {
      return `${days}d ${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  };

  const formatBytes = (bytes: number): string => {
    const sizes = ['B', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 B';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
  };

  const getMemoryUsagePercentage = (used: number, total: number): number => {
    return Math.round((used / total) * 100);
  };

  const getMemoryStatusColor = (percentage: number): string => {
    if (percentage < 60) return 'good';
    if (percentage < 80) return 'warning';
    return 'critical';
  };

  const refreshAll = () => {
    refreshHealth();
    refreshSystemInfo();
  };

  return (
    <div className={styles.systemMonitor}>
      <div className={styles.monitorHeader}>
        <h2>📊 System Monitor</h2>
        <button 
          onClick={refreshAll}
          disabled={healthLoading || systemLoading}
          className="btn btn-secondary btn-small"
          title="Refresh all data"
        >
          {healthLoading || systemLoading ? '↻' : '🔄'}
        </button>
      </div>

      {/* Health Status */}
      <div className={styles.monitorSection}>
        <h3>🏥 System Health</h3>
        
        {healthError && (
          <div className={styles.monitorError}>
            <span className={styles.errorIcon}>⚠️</span>
            <span className={styles.errorText}>{healthError}</span>
            <button onClick={clearHealthError} className={styles.errorClose}>×</button>
          </div>
        )}

        {health && (
          <div className={styles.healthGrid}>
            <div className={styles.healthItem}>
              <div className={styles.healthLabel}>Status</div>
              <div className={`${styles.healthValue} ${styles.statusGood}`}>
                ✅ Operational
              </div>
            </div>
            
            <div className={styles.healthItem}>
              <div className={styles.healthLabel}>Environment</div>
              <div className={styles.healthValue}>
                <span className={`${styles.envBadge} ${styles[`env${health.environment.charAt(0).toUpperCase() + health.environment.slice(1)}`]}`}>
                  {health.environment.toUpperCase()}
                </span>
              </div>
            </div>
            
            <div className={styles.healthItem}>
              <div className={styles.healthLabel}>Last Check</div>
              <div className={styles.healthValue}>
                {new Date(health.timestamp).toLocaleTimeString()}
              </div>
            </div>

            <div className={styles.healthItem}>
              <div className={styles.healthLabel}>Maintenance Mode</div>
              <div className={styles.healthValue}>
                <span className={`${styles.maintenanceBadge} ${health.maintenance.enabled ? styles.enabled : styles.disabled}`}>
                  {health.maintenance.enabled ? '🔧 Active' : '✅ Disabled'}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* System Information */}
      <div className={styles.monitorSection}>
        <h3>💻 System Information</h3>
        
        {systemError && (
          <div className={styles.monitorError}>
            <span className={styles.errorIcon}>⚠️</span>
            <span className={styles.errorText}>{systemError}</span>
            <button onClick={clearSystemError} className={styles.errorClose}>×</button>
          </div>
        )}

        {systemInfo && (
          <div className={styles.systemGrid}>
            {/* Basic Info */}
            <div className={styles.systemSection}>
              <h4>📋 Basic Info</h4>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Node Version:</span>
                <span className={styles.infoValue}>{systemInfo.system.nodeVersion}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Platform:</span>
                <span className={styles.infoValue}>{systemInfo.system.platform}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Uptime:</span>
                <span className={styles.infoValue}>{formatUptime(systemInfo.system.uptime)}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Environment:</span>
                <span className={styles.infoValue}>
                  <span className={`${styles.envBadge} ${styles[`env${systemInfo.system.environment.charAt(0).toUpperCase() + systemInfo.system.environment.slice(1)}`]}`}>
                    {systemInfo.system.environment.toUpperCase()}
                  </span>
                </span>
              </div>
            </div>

            {/* Memory Usage */}
            <div className={styles.systemSection}>
              <h4>💾 Memory Usage</h4>
              
              <div className={styles.memoryItem}>
                <div className={styles.memoryHeader}>
                  <span className={styles.memoryLabel}>Heap Used</span>
                  <span className={styles.memoryValue}>
                    {formatBytes(systemInfo.system.memory.heapUsed)} / {formatBytes(systemInfo.system.memory.heapTotal)}
                  </span>
                </div>
                <div className={styles.memoryBar}>
                  <div 
                    className={`${styles.memoryFill} ${styles[getMemoryStatusColor(
                      getMemoryUsagePercentage(
                        systemInfo.system.memory.heapUsed, 
                        systemInfo.system.memory.heapTotal
                      )
                    ) as keyof typeof styles]}`}
                    style={{ 
                      width: `${getMemoryUsagePercentage(
                        systemInfo.system.memory.heapUsed, 
                        systemInfo.system.memory.heapTotal
                      )}%` 
                    }}
                  ></div>
                </div>
                <div className={styles.memoryPercentage}>
                  {getMemoryUsagePercentage(
                    systemInfo.system.memory.heapUsed, 
                    systemInfo.system.memory.heapTotal
                  )}%
                </div>
              </div>

              <div className={styles.memoryDetails}>
                <div className={styles.memoryDetailItem}>
                  <span className={styles.detailLabel}>RSS:</span>
                  <span className={styles.detailValue}>{formatBytes(systemInfo.system.memory.rss)}</span>
                </div>
                <div className={styles.memoryDetailItem}>
                  <span className={styles.detailLabel}>External:</span>
                  <span className={styles.detailValue}>{formatBytes(systemInfo.system.memory.external)}</span>
                </div>
                <div className={styles.memoryDetailItem}>
                  <span className={styles.detailLabel}>Array Buffers:</span>
                  <span className={styles.detailValue}>{formatBytes(systemInfo.system.memory.arrayBuffers)}</span>
                </div>
              </div>
            </div>

            {/* Maintenance Status */}
            <div className={styles.systemSection}>
              <h4>🔧 Maintenance Status</h4>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Status:</span>
                <span className={styles.infoValue}>
                  <span className={`${styles.maintenanceBadge} ${systemInfo.maintenance.enabled ? styles.enabled : styles.disabled}`}>
                    {systemInfo.maintenance.enabled ? 'Active' : 'Disabled'}
                  </span>
                </span>
              </div>
              {systemInfo.maintenance.enabled && systemInfo.maintenance.message && (
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Message:</span>
                  <span className={styles.infoValue}>{systemInfo.maintenance.message}</span>
                </div>
              )}
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Last Updated:</span>
                <span className={styles.infoValue}>
                  {new Date(systemInfo.maintenance.timestamp).toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Loading States */}
      {(healthLoading || systemLoading) && (
        <div className={styles.monitorLoading}>
          <div className={styles.loadingIndicator}>
            <div className={styles.loadingSpinner}></div>
            <span>Refreshing system data...</span>
          </div>
        </div>
      )}

      {/* Auto-refresh info */}
      <div className={styles.monitorFooter}>
        <small>📡 Auto-refreshes every 30 seconds</small>
      </div>
    </div>
  );
};

export default SystemMonitor;